<template>
  <main class="editor">
    <header class="caption">
      <div class="toggle-menu">
        <div>
          <v-tooltip right>
            <template v-slot:activator="{ on }">
              <v-btn icon dark v-on="on" @click.prevent="hideNavigator = !hideNavigator">
                <v-icon dark>mdi-menu</v-icon>
              </v-btn>
            </template>
            <span>
              <span v-if="hideNavigator">Show</span>
              <span v-else>Hide</span> Navigator
            </span>
          </v-tooltip>
        </div>
      </div>

      <div class="title">
        <h1 class="headline">{{slide.title}}</h1>
      </div>

      <div class="action-menu">
        <v-btn text :disabled="!dirty" v-on="on" @click.prevent="save" color="orange" dark>Save</v-btn>

        <v-btn icon color="white" @click="hideProps = !hideProps">
          <v-icon v-if="hideProps">mdi-backburger</v-icon>
          <v-icon v-else>mdi-menu</v-icon>
        </v-btn>

        <v-tooltip bottom>
          <template v-slot:activator="{ on }">
            <v-btn icon dark v-on="on" color="orange">
              <v-icon dark>mdi-play-box-outline</v-icon>
            </v-btn>
          </template>
          <span>Play Presentation</span>
        </v-tooltip>
        <v-tooltip bottom>
          <template v-slot:activator="{ on }">
            <v-btn
              icon
              dark
              v-on="on"
              :to="`/event/${$route.params.eventid}/screens`"
              color="orange"
            >
              <v-icon dark>mdi-close</v-icon>
            </v-btn>
          </template>
          <span>Close</span>
        </v-tooltip>
      </div>
    </header>
    <aside class="toolbox">
      <section class="actions">
        <div class="item">
          <v-tooltip right>
            <template v-slot:activator="{ on }">
              <v-btn icon dark v-on="on" @click="addTextBox">
                <v-icon dark>mdi-format-text</v-icon>
              </v-btn>
            </template>
            <span>Add Textbox</span>
          </v-tooltip>
        </div>
        <div class="item">
          <v-tooltip right>
            <template v-slot:activator="{ on }">
              <v-btn icon dark v-on="on">
                <v-icon dark>mdi-format-textbox</v-icon>
              </v-btn>
            </template>
            <span>Tooltip</span>
          </v-tooltip>
        </div>
        <div class="item">
          <v-tooltip right>
            <template v-slot:activator="{ on }">
              <v-btn icon dark v-on="on" disabled>
                <v-icon dark>mdi-shape-rectangle-plus</v-icon>
              </v-btn>
            </template>
            <span>Tooltip</span>
          </v-tooltip>
        </div>
        <div class="item">
          <v-tooltip right>
            <template v-slot:activator="{ on }">
              <v-btn icon dark v-on="on" @click="addImageBox">
                <v-icon dark>mdi-image-plus</v-icon>
              </v-btn>
            </template>
            <span>Tooltip</span>
          </v-tooltip>
        </div>
      </section>

      <section class="secondary-actions"></section>
    </aside>

    <section class="slide">
      <aside :class="{
        navigator: true,
        hide: hideNavigator
      }">
        <section class="thumbnails">
          <draggable v-model="slide.slides" :dragoverBubble="true" :delay="20">
            <v-slide-y-transition group dense tag="v-list" shaped style="background:none;">
              <v-list-item
                dense
                v-for="(item) in slide.slides"
                :key="item.uuid"
                link
                :to="'/editor/' + $route.params.eventid+ '/slide/' + $route.params.slideid + '/' + item.uuid"
                exact
              >
                <v-list-item-content>
                  <v-card style="border: 1px solid rgba(0,0,0,0.2" color="white ">
                    <v-img :src="item.backgroundImage ? item.backgroundImage : '@/assets/rainbow.jpg'" height="100px"></v-img>
                    <v-card-title class="caption black--text">{{ item.name }}</v-card-title>
                  </v-card>
                </v-list-item-content>
              </v-list-item>
              <!--v-divider key="dev"></v-divider>
            <v-list-item link key="asdf">
              <v-list-item-icon>
                <v-icon>mdi-plus</v-icon>
              </v-list-item-icon>
              <v-list-item-action @click="addSlide = !addSlide">
                <v-list-item-action-text>add new slide</v-list-item-action-text>
              </v-list-item-action>
              </v-list-item-->
            </v-slide-y-transition>
          </draggable>
        </section>

        <section class="newslide">
          <v-divider></v-divider>
          <v-btn icon color="orange" @click="addSlide = !addSlide">
            <v-icon>mdi-plus</v-icon>
          </v-btn>
        </section>
        <AddSlideDialog v-model="addSlide"></AddSlideDialog>
      </aside>

      <section :class="{
        canvas: true,
        full: hideProps
      }">
        <router-view></router-view>
      </section>

      <aside :class="{
        properties: true,
        hide: hideProps
      }">
        <PropertyEditor></PropertyEditor>
      </aside>
    </section>
    <footer>
      <v-btn icon dark @click="zoomIn">
        <v-icon>mdi-magnify-plus-outline</v-icon>
      </v-btn>
      <span class="white--text">{{ zoom }}%</span>
      <v-btn icon dark @click="zoomOut">
        <v-icon>mdi-magnify-minus-outline</v-icon>
      </v-btn>
    </footer>
  </main>
</template>

<style scoped lang="scss">
/**
 * color theme from: https://github.com/dracula/dracula-theme
 */
//$background: hsl(231°, 15%, 18%);
$background: rgba(0, 0, 0, 0.8);
$currentLine: hsl(232°, 14%, 31%);
$selection: hsl(232°, 14%, 31%);
$foreground: hsl(60°, 30%, 96%);
$comment: hsl(225°, 27%, 51%);
$cyan: hsl(191°, 97%, 77%);
$green: hsl(135°, 94%, 65%);
$orange: hsl(31°, 100%, 71%);
$pink: hsl(326°, 100%, 74%);
$purple: hsl(265°, 89%, 78%);
$red: hsl(0°, 100%, 67%);
$yellow: hsl(65°, 92%, 76%);

$line: lighten($background, 40%);

$dark-border: rgba(40, 42, 54, 0.2);

$navigator-width: 200px;
$properties-width: 250px;

.theme--dark {
  &.v-icon {
    color: $foreground;
  }
}
main {
  &.editor {
    display: grid;

    grid-template-columns: 50px auto;
    grid-auto-rows: 40px auto 30px;
    grid-template-areas:
      "header  header"
      "toolbox  slide"
      "footer  footer";
    background-color: white;

    height: 100vh;
    width: 100vw;

    header {
      display: grid;
      grid-area: header;
      background-color: $background;
      color: white;
      border-bottom: 1px solid $line;
      grid-template-columns: 50px $navigator-width auto $properties-width;
      grid-auto-rows: auto;
      grid-template-areas: "toggle mainactions title action";

      .main-actions {
        grid-area: mainactions;
        display: flex;
        align-items: center;
        justify-content: center;
        color: $orange;
        border: 1px solid $red;
      }

      .toggle-menu {
        grid-area: toggle;
        display: flex;
        align-items: center;
        flex-direction: column;
      }
      .title {
        grid-area: title;
        display: flex;
        align-items: center;
        justify-content: center;
        color: $orange;
        h1 {
          border: 1px solid transparent;
          &:hover {
            border: 1px solid $pink;
          }
        }
      }

      .action-menu {
        grid-area: action;
        display: flex;
        justify-content: flex-end;
      }
    }
    .toolbox {
      grid-area: toolbox;
      background-color: $background;
      border-right: 1px solid $line;
      color: $foreground;
      overflow: hidden;
      display: grid;
      .actions {
        display: flex;
        flex-direction: column;
        align-items: center;
        & > .item {
          margin-top: 5px;
          height: 40px;
          overflow: hidden;
        }
      }
      .secondary-actions {
        display: flex;
      }
    }
    .slide {
      grid-area: slide;

      display: flex;
      overflow: hidden;

      .navigator {
        width: $navigator-width;
        background-color: lighten($background, 5%);
        border-right: 1px solid $line;
        transition: all 0.2s ease-in-out;
        display: grid;
        grid-template-columns: auto;
        grid-auto-rows: auto 35px;
        grid-template-areas:
          "thumbnails"
          "newslide";

        &.hide {
          margin-left: -$navigator-width;
        }

        .thumbnails {
          grid-area: thumbnails;
          overflow: auto;
          .v-list-item {
            transition: background .8s ease;
          }
          .v-list-item--active {
            background: rgba($orange, 1);
          }
          &::-webkit-scrollbar {
            width: 0 !important;
          }
        }
        .newslide {
          grid-area: newslide;
          display: flex;
          align-items: flex-end;
        }
      }

      .canvas {
        flex: 1;
        background-color: lighten($background, 60%);
        overflow: hidden;
        &.full {
        }

        display: flex;
        justify-content: center;
        align-items: center;
      }

      .properties {
        width: $properties-width;
        overflow: hidden;
        background-color: $background;
        border-left: 1px solid $line;
        transition: all 0.2s ease-out;
        &.hide {
          width: 0px;

          transform: translateX(100%);
        }
      }
    }

    footer {
      grid-area: footer;
      border-top: 1px solid $line;
      background-color: $background;
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: flex-end;
    }
  }
}
</style>
<script>
import { mapState, mapActions } from "vuex";
const uuidv4 = require("uuid/v4");
import draggable from "vuedraggable";
import PropertyEditor from "@/components/event/slides/PropertyEditor";
import AddSlideDialog from "@/components/event/slides/AddSlideDialog";
export default {
  components: {
    PropertyEditor,
    draggable,
    AddSlideDialog
  },
  data() {
    return {
      slide: {},
      addSlide: false,
      hideProps: false,
      hideNavigator: false,
      zoomLevel: 1,
      dirty: false
    };
  },
  methods: {
    ...mapActions("events", ["insert", "set", "delete", "fetchById"]),
    ...mapActions("slides", ["insert", "set", "delete", "fetchById", "patch"]),
    ...mapActions("application", ["zoomIn", "zoomOut"]),

    async addImageBox() {
      let component = {
        uuid: uuidv4(),
        type: "Image",
        src: "https://via.placeholder.com/200x300?text=Add+Image",
        x: 200,
        y: 300,
        backgroundColor: {
          hexa: "#fffffffa"
        },
        foreGroundColor: {
          hexa: "#000000fC"
        },
        width: "200px",
        height: "500px",
        align: "left",
        animationIn: "fadeIn",
        animationOut: "fadeOut"
      };

      let pageid = this.$route.params.slide;

      if (pageid) {
        let page = this.slide.slides.find(item => item.uuid === pageid);
        page.components.push(component);

        console.log(page);
      }
    },

    async addTextBox() {
      let component = {
        uuid: uuidv4(),
        type: "Text",
        text: "sample text",
        x: 500,
        y: 500,
        backgroundColor: {
          hexa: "#ffffffff"
        },
        foreGroundColor: {
          hexa: "#000000ff"
        },
        width: "200px",
        height: "500px",
        align: "left",
        animationIn: "fadeIn",
        animationOut: "fadeOut"
      };

      let pageid = this.$route.params.slide;

      if (pageid) {
        let page = this.slide.slides.find(item => item.uuid === pageid);
        page.components.push(component);

        console.log(page);
      }
    },
    async save() {
      try {
        let data = {
          id: this.$route.params.slideid,
          ...this.slide
        };

        await this.patch(data);
        //this.dirty = false;
      } catch (e) {
        alert(e);
      }
    },
    triggerResizeEvent() {
      window.setTimeout(() => {
        window.dispatchEvent(new Event("resize"));
      }, 400);
    }
  },

  watch: {
    slide(val) {
      if (!this.dirty) {
        this.dirty = true;
      }
    },
    hideNavigator(val) {
      this.triggerResizeEvent();
    },
    hideProps(val) {
      this.triggerResizeEvent();
    }
  },
  computed: {
    slided() {
      const slideid = this.$route.params.slideid;
      return this.$store.state.slides.data[slideid];
    },
    zoom() {
      return parseInt(this.$store.state.application.zoomLevel * 100);
    }
  },

  async created() {
    let eventId = this.$route.params.eventid;
    let slideId = this.$route.params.slideid;
    this.$store
      .dispatch("events/openDBChannel", {
        where: [["event", "==", eventId]]
      })
      .catch(e => {
        console.log("dispatched called with error");
        console.log(e);
      });

    let slide = await this.fetchById(slideId);
    this.slide = slide;
    console.log(slide);
  }
};
</script>